import React, { useEffect, useRef, useState } from "react";
import {
  FaWhatsapp,
  FaClock,
  FaRunning,
  FaSwimmer,
  FaBiking,
  FaChartLine,
  FaCheck,
  FaArrowRight,
} from "react-icons/fa";
import bruno_brito from "../assets/Triathlon/bruno_brito.png";
import triathlon_ironman from "../assets/Triathlon/triathlon_ironman.png";
import triathlon_maratona from "../assets/Triathlon/triathlon_maratona.png";
import triathlon_olimpico from "../assets/Triathlon/triathlon_olimpico.png";
import triathlon_sprint from "../assets/Triathlon/triathlon_sprint.png";
import Seo from "../seo/Seo";
import { seoConfig } from "../seo/seoConfig";
import "../css/Triathlon.css";

// Event Card Component
const EventCard = ({
  image,
  title,
  duration,
  schedule,
  intensity,
  delay,
  featured,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setIsVisible(true), delay);
        }
      },
      { threshold: 0.1 },
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, [delay]);

  return (
    <div
      ref={cardRef}
      className={`event-card ${isVisible ? "visible" : ""} ${featured ? "featured" : ""}`}
    >
      {featured && <span className="featured-badge">Popular</span>}
      <div className="event-image-wrapper">
        <img src={image} alt={title} className="event-image" />
      </div>
      <div className="event-content">
        <h3 className="event-title">{title}</h3>
        <div className="event-details">
          <div className="event-detail">
            <FaClock className="detail-icon" />
            <span>{duration}</span>
          </div>
          <div className="event-detail">
            <FaChartLine className="detail-icon" />
            <span>{intensity}</span>
          </div>
        </div>
        <p className="event-schedule">{schedule}</p>
        <a
          href={`https://wa.me/+351962281179?text=${encodeURIComponent(`Olá! Gostaria de saber mais sobre o plano ${title}.`)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="event-cta"
        >
          Saber Mais <FaArrowRight />
        </a>
      </div>
    </div>
  );
};

const Triathlon = () => {
  const eventsData = [
    {
      id: 1,
      image: triathlon_maratona,
      title: "Plano Maratona",
      duration: "36 semanas",
      schedule: "4 corridas + 1 força por semana",
      intensity: "+5h treino/semana",
      featured: false,
    },
    {
      id: 2,
      image: triathlon_sprint,
      title: "Super Sprint Triathlon",
      duration: "12 semanas",
      schedule: "2 corridas + 2 natação + 2 bike",
      intensity: "+4.5h treino/semana",
      featured: false,
    },
    {
      id: 3,
      image: triathlon_olimpico,
      title: "Triathlon Olímpico",
      duration: "20 semanas",
      schedule: "3 corridas + 3 natação + 3 bike",
      intensity: "+10h treino/semana",
      featured: true,
    },
    {
      id: 4,
      image: triathlon_ironman,
      title: "Ironman 70.3",
      duration: "28 semanas",
      schedule: "3 corridas + 3 natação + 3 bike",
      intensity: "+15h treino/semana",
      featured: false,
    },
  ];

  return (
    <div className="triathlon-page">
      <Seo {...seoConfig.triathlon} />
      {/* Hero Section */}
      <section className="triathlon-hero">
        <div className="hero-overlay"></div>
        <div className="container">
          <div className="hero-content">
            <span className="hero-tag">Triathlon Coaching</span>
            <h1 className="hero-title">
              Supere os Seus <span className="highlight">Limites</span>
            </h1>
            <p className="hero-subtitle">
              Alcance o seu potencial máximo como atleta de triathlon com a
              nossa orientação especializada e personalizada.
            </p>
            <div className="hero-stats">
              <div className="stat-item">
                <FaSwimmer className="stat-icon" />
                <span>Natação</span>
              </div>
              <div className="stat-item">
                <FaBiking className="stat-icon" />
                <span>Ciclismo</span>
              </div>
              <div className="stat-item">
                <FaRunning className="stat-icon" />
                <span>Corrida</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Coaching Section */}
      <section className="coaching-section">
        <div className="container">
          <div className="coaching-grid">
            <div className="coaching-content">
              <div className="section-header">
                <span className="section-tag">O Nosso Método</span>
                <h2>Coaching Personalizado</h2>
              </div>
              <p className="coaching-text">
                O <strong>triathlon coaching</strong> é um processo em que um
                treinador especializado trabalha consigo para desenvolver um
                plano de treino personalizado, acompanhando o seu progresso e
                fazendo ajustes conforme necessário.
              </p>
              <ul className="coaching-features">
                <li>
                  <FaCheck className="check-icon" />
                  <span>Plano de treino personalizado</span>
                </li>
                <li>
                  <FaCheck className="check-icon" />
                  <span>Acompanhamento contínuo do progresso</span>
                </li>
                <li>
                  <FaCheck className="check-icon" />
                  <span>Aconselhamento nutricional</span>
                </li>
                <li>
                  <FaCheck className="check-icon" />
                  <span>Planeamento de competições</span>
                </li>
              </ul>
            </div>
            <div className="coach-card">
              <div className="coach-image-wrapper">
                <img
                  src={bruno_brito}
                  alt="Bruno Brito"
                  className="coach-image"
                />
                <div className="coach-badge">Head Coach</div>
              </div>
              <div className="coach-info">
                <h3>Bruno Brito</h3>
                <p>Treinador Certificado de Triathlon</p>
                <a
                  href={`https://wa.me/+351962281179?text=${encodeURIComponent("Olá! Gostaria de falar com o coach Bruno Brito sobre triathlon coaching.")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="coach-cta"
                >
                  <FaWhatsapp /> Contactar Coach
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Events Section */}
      <section className="events-section">
        <div className="container">
          <div className="section-header centered">
            <span className="section-tag">Planos de Treino</span>
            <h2>Escolha o Seu Evento</h2>
            <p className="section-description">
              Selecione o plano que melhor se adapta aos seus objetivos e nível
              de experiência.
            </p>
          </div>
          <div className="events-grid">
            {eventsData.map((event, index) => (
              <EventCard
                key={event.id}
                image={event.image}
                title={event.title}
                duration={event.duration}
                schedule={event.schedule}
                intensity={event.intensity}
                featured={event.featured}
                delay={index * 100}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="why-section">
        <div className="container">
          <div className="why-content">
            <div className="section-header centered">
              <span className="section-tag">Porquê Escolher-nos</span>
              <h2>Triathlon Coaching é Ideal Para Si</h2>
            </div>
            <p className="why-text">
              Aderir ao nosso plano de triathlon coaching pode ajudá-lo a
              atingir os seus objetivos na modalidade, maximizar o seu potencial
              e desempenho, bem como oferecer aconselhamento e orientação em
              várias áreas relevantes para a prática desportiva.
            </p>
            <div className="triathlon-cta">
              <h3>Pronto para Começar?</h3>
              <a
                href={`https://wa.me/+351962281179?text=${encodeURIComponent("Olá! Gostaria de começar o triathlon coaching.")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="primary-cta"
              >
                <FaWhatsapp /> Fale Connosco
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Triathlon;
